import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/3d2cujiq_MAIN%20%283%29.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Outreach', path: '/outreach' },
  { name: 'Contact', path: '/contact' },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <header role="banner">
      <nav 
        className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100 py-4"
        data-testid="navbar"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center" 
              data-testid="navbar-logo"
              aria-label="KTM Construction - Go to homepage"
            >
              <img 
                src={LOGO_URL} 
                alt="KTM Construction Logo" 
                className="h-48 w-auto"
                data-logo="true"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" aria-label="Primary navigation">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  aria-current={location.pathname === link.path ? 'page' : undefined}
                  className={`font-manrope font-semibold text-sm uppercase tracking-wider transition-colors ${
                    location.pathname === link.path
                      ? 'text-ktm-red'
                      : 'text-ktm-navy hover:text-ktm-red'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              <a 
                href="tel:916-872-1882" 
                className="flex items-center gap-2 text-ktm-navy font-semibold"
                data-testid="navbar-phone"
                aria-label="Call us at 916-872-1882"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                <span>916-872-1882</span>
              </a>
              <Link to="/contact?quote=true">
                <Button 
                  className="bg-ktm-navy text-white h-10 px-6 rounded-none font-bold uppercase tracking-wider btn-shadow hover:bg-ktm-navy/90"
                  data-testid="navbar-quote-btn"
                  aria-label="Get a free quote"
                >
                  Free Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
              aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isOpen}
              aria-controls="mobile-navigation"
            >
              {isOpen ? (
                <X className="w-6 h-6 text-ktm-navy" aria-hidden="true" />
              ) : (
                <Menu className="w-6 h-6 text-ktm-navy" aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                id="mobile-navigation"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="lg:hidden overflow-hidden"
                data-testid="mobile-menu"
                role="navigation"
                aria-label="Mobile navigation"
              >
                <div className="pt-6 pb-4 flex flex-col gap-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                      aria-current={location.pathname === link.path ? 'page' : undefined}
                      className={`font-manrope font-semibold text-base uppercase tracking-wider py-3 border-b border-slate-100 ${
                        location.pathname === link.path
                          ? 'text-ktm-red'
                          : 'text-ktm-navy'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                  <div className="flex flex-col gap-3 pt-4">
                    <a 
                      href="tel:916-872-1882" 
                      className="flex items-center gap-2 text-ktm-navy font-semibold py-2"
                      aria-label="Call us at 916-872-1882"
                    >
                      <Phone className="w-4 h-4" aria-hidden="true" />
                      <span>916-872-1882</span>
                    </a>
                    <Link to="/contact?quote=true" onClick={() => setIsOpen(false)}>
                      <Button 
                        className="w-full bg-ktm-navy text-white h-12 px-6 rounded-none font-bold uppercase tracking-wider btn-shadow"
                        aria-label="Get a free quote"
                      >
                        Free Quote
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
